import DOMPurify from "dompurify";

export function stripHtmlToText(html: string): string {
  const raw = String(html ?? "");
  if (!raw) return "";

  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    return (doc.body?.textContent ?? "").replace(/\u00a0/g, " ");
  } catch {
    return raw
      .replace(/<style[\s\S]*?<\/style>/gi, " ")
      .replace(/<script[\s\S]*?<\/script>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
}

export function escapeHtmlText(text: string): string {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function decodeHtmlEntities(input: string, passes = 2): string {
  let out = String(input ?? "");
  for (let i = 0; i < passes; i++) {
    try {
      const doc = new DOMParser().parseFromString(`<div>${out}</div>`, "text/html");
      const next = (doc.body?.textContent ?? out).replace(/\u00a0/g, " ");
      if (next === out) break;
      out = next;
    } catch {
      break;
    }
  }
  return out;
}

export function coercePlainTextToHtml(input: string): string {
  const raw = String(input ?? "");
  if (!raw) return "";

  // If it already looks like HTML, keep it as-is.
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) return raw;

  // If it contains HTML entities already, treat it as HTML text (avoid double-escaping).
  if (/&(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);/.test(raw)) return raw;

  // Otherwise treat as plain text and preserve newlines.
  const decoded = decodeHtmlEntities(raw, 2);
  return escapeHtmlText(decoded).replace(/\r\n|\n|\r/g, "<br/>");
}

export function sanitizeRichHtml(html: string): string {
  const raw = String(html ?? "");
  if (!raw.trim()) return "";

  // Keep this deliberately tight: only what's needed for Bold/Italic/Color.
  return DOMPurify.sanitize(raw, {
    ALLOWED_TAGS: [
      "b",
      "strong",
      "i",
      "em",
      "u",
      "span",
      "font",
      "br",
      "p",
      "div",
    ],
    ALLOWED_ATTR: ["style", "color"],
    FORBID_TAGS: ["img", "svg", "math", "video", "audio", "iframe"],
  });
}

export function sanitizeRichHtmlOrPlainText(input: string): string {
  const html = coercePlainTextToHtml(String(input ?? ""));
  return sanitizeRichHtml(html);
}
